<template>
    <div class="filter-builder">
        <div v-if="!localConditions.filterGroups || localConditions.filterGroups.length === 0" class="empty-filters">
            <div class="empty-icon">
                <i class="material-icons">filter_list_off</i>
            </div>
            <p>{{ $t("contactModuleRules.filterBuilder.noConditions") }}</p>
            <a-button type="primary" @click="addFilterGroup">
                <i class="material-icons">add</i>
                {{ $t("contactModuleRules.filterBuilder.addFirstCondition") }}
            </a-button>
        </div>

        <div v-else class="filter-body">
            <!-- Filter Groups -->
            <div
                v-for="(group, groupIndex) in localConditions.filterGroups"
                :key="`group-${groupIndex}`"
                class="filter-group-container"
            >
                <div class="filter-group">
                    <!-- Group Header -->
                    <div v-if="localConditions.filterGroups.length > 1" class="group-header">
                        <span class="group-title">{{ $t("contactModuleRules.filterBuilder.groupTitle", { n: groupIndex + 1 }) }}</span>
                        <a-button
                            type="text"
                            icon="delete"
                            size="small"
                            danger
                            @click="removeGroup(groupIndex)"
                        >
                            {{ $t("contactModuleRules.filterBuilder.deleteGroup") }}
                        </a-button>
                    </div>

                    <!-- Filters in Group -->
                    <div
                        v-for="(filter, filterIndex) in group.filters"
                        :key="`filter-${filterIndex}`"
                        class="filter-row"
                    >
                        <span class="filter-text">
                            <!-- Prefix -->
                            <span v-if="filterIndex === 0" class="prefix">{{ $t("contactModuleRules.filterBuilder.if") }}</span>
                            <InlineSelectOperator
                                v-else
                                v-model="group.operator"
                                :options="[
                                    { label: $t('contactModuleRules.filterBuilder.andOption'), value: 'AND' },
                                    { label: $t('contactModuleRules.filterBuilder.orOption'), value: 'OR' }
                                ]"
                                class="prefix-operator"
                                @change="emitChange"
                            />

                            <!-- Field -->
                            <InlineSelect
                                v-model="filter.field"
                                :options="getAvailableFields()"
                                :placeholder="$t('contactModuleRules.filterBuilder.selectField')"
                                :searchable="true"
                                @change="onFieldChange(filter)"
                            />

                            <!-- Operator -->
                            <InlineSelectOperator
                                v-if="filter.field"
                                v-model="filter.operator"
                                :options="getOperatorsForField(filter.field)"
                                :placeholder="$t('contactModuleRules.filterBuilder.operator')"
                                @change="emitChange"
                            />

                            <!-- Value -->
                            <template v-if="filter.field && filter.operator && needsValue(filter.operator)">
                                <!-- For array/multi-select -->
                                <span v-if="isMultiSelect(filter)" class="multi-value-wrapper">
                                    <a-select
                                        v-model="filter.value"
                                        mode="multiple"
                                        class="multi-select-inline"
                                        :placeholder="$t('contactModuleRules.filterBuilder.select')"
                                        @change="emitChange"
                                    >
                                        <a-select-option 
                                            v-for="opt in getFieldOptions(filter.field)" 
                                            :key="opt.value" 
                                            :value="opt.value"
                                        >
                                            {{ opt.label }}
                                        </a-select-option>
                                    </a-select>
                                </span>

                                <!-- For single-select with options -->
                                <InlineSelectValue
                                    v-else-if="hasOptions(filter.field)"
                                    v-model="filter.value"
                                    :options="getFieldOptions(filter.field)"
                                    :placeholder="$t('contactModuleRules.filterBuilder.selectValue')"
                                    :searchable="true"
                                    @change="emitChange"
                                />

                                <!-- Boolean -->
                                <InlineSelectValue
                                    v-else-if="getFieldDataType(filter.field) === 'boolean'"
                                    v-model="filter.value"
                                    :options="[
                                        { label: 'Evet', value: true },
                                        { label: 'Hayır', value: false }
                                    ]"
                                    :placeholder="$t('contactModuleRules.filterBuilder.select')"
                                    @change="emitChange"
                                />

                                <!-- Regular text/number input -->
                                <InlineInput
                                    v-else
                                    v-model="filter.value"
                                    :type="getFieldDataType(filter.field) === 'number' ? 'number' : 'text'"
                                    :placeholder="$t('contactModuleRules.filterBuilder.enterValue')"
                                    @change="emitChange"
                                />
                            </template>
                        </span>

                        <!-- Remove Button -->
                        <a-button
                            type="text"
                            size="small"
                            class="remove-btn"
                            @click="removeFilter(groupIndex, filterIndex)"
                        >
                            <i class="material-icons">delete_outline</i>
                        </a-button>
                    </div>

                    <!-- Add Filter Button -->
                    <div class="add-condition-btn" @click="addFilterToGroup(groupIndex)">
                        <i class="material-icons">add</i>
                        <span>{{ $t("contactModuleRules.filterBuilder.addCondition") }}</span>
                    </div>
                </div>

                <!-- Group Operator -->
                <div
                    v-if="groupIndex < localConditions.filterGroups.length - 1"
                    class="group-operator-row"
                >
                    <div class="separator-line"></div>
                    <div class="operator-badge">
                        <a-select
                            v-model="localConditions.groupOperator"
                            size="small"
                            class="operator-selector"
                            @change="emitChange"
                        >
                            <a-select-option value="AND">{{ $t("contactModuleRules.AND") }} (AND)</a-select-option>
                            <a-select-option value="OR">{{ $t("contactModuleRules.OR") }} (OR)</a-select-option>
                        </a-select>
                    </div>
                    <div class="separator-line"></div>
                </div>
            </div>

            <!-- Add Group Button -->
            <div class="add-group-btn" @click="addFilterGroup">
                <i class="material-icons">add_circle_outline</i>
                <span>{{ $t("contactModuleRules.filterBuilder.addFilterGroup") }}</span>
            </div>
        </div>
    </div>
</template>

<script>
import { mapState } from "vuex";
import { generateRandomId } from "../../../../../../utils/session";
import InlineSelect from "../../../../Automation/components/InlineSelect.vue";
import InlineSelectOperator from "../../../../Automation/components/InlineSelectOperator.vue";
import InlineSelectValue from "../../../../Automation/components/InlineSelectValue.vue";
import InlineInput from "../../../../Automation/components/InlineInput.vue";
import log2 from "../../../../../../utils/log2";
import _ from "lodash";

const log = log2("FilterBuilder");

export default {
    name: "FilterBuilder",
    components: {
        InlineSelect,
        InlineSelectOperator,
        InlineSelectValue,
        InlineInput
    },
    props: {
        value: {
            type: Object,
            required: true
        },
        whenType: {
            type: String,
            required: true
        }
    },
    data() {
        return {
            localConditions: {
                filterGroups: [],
                groupOperator: "AND"
            }
        };
    },
    computed: {
        ...mapState({
            tags: state => state.tag.tags.data || [],
            users: state => state.user.users.data || [],
            subjects: state => state.subject.subjects.data || [],
            channels: state => state.channel.channels || []
        }),
        departments() {
            return this.subjects
                .filter(s => !s.isDeleted && s.isActive)
                .map(s => ({
                    label: s.languageContent[this.$i18n.locale].name,
                    value: s._id
                }));
        },
        contactTags() {
            return this.tags
                .filter(t => t.type === 'contact')
                .map(t => ({
                    label: t.name,
                    value: t._id
                }));
        }
    },
    watch: {
        value: {
            handler(newVal) {
                if (newVal && !_.isEqual(newVal, this.localConditions)) {
                    this.localConditions = _.cloneDeep(newVal);
                }
            },
            immediate: true,
            deep: true
        }
    },
    methods: {
        emitChange() {
            this.$emit("input", _.cloneDeep(this.localConditions));
        },

        addFilterGroup() {
            const newGroup = {
                id: generateRandomId(),
                operator: "AND",
                filters: [{
                    id: generateRandomId(),
                    field: "",
                    operator: "equals",
                    value: null,
                    dataType: "string"
                }]
            };
            this.localConditions.filterGroups.push(newGroup);
            this.emitChange();
        },

        removeGroup(groupIndex) {
            this.localConditions.filterGroups.splice(groupIndex, 1);
            if (this.localConditions.filterGroups.length === 0) {
                this.localConditions.filterGroups = [];
            }
            this.emitChange();
        },

        addFilterToGroup(groupIndex) {
            const newFilter = {
                id: generateRandomId(),
                field: "",
                operator: "equals",
                value: null,
                dataType: "string"
            };
            this.localConditions.filterGroups[groupIndex].filters.push(newFilter);
            this.emitChange();
        },

        removeFilter(groupIndex, filterIndex) {
            const group = this.localConditions.filterGroups[groupIndex];
            if (group.filters.length === 1 && this.localConditions.filterGroups.length > 1) {
                // Son filter ve birden fazla grup varsa grubu sil
                this.removeGroup(groupIndex);
            } else if (group.filters.length > 1) {
                // Sadece filter'ı sil
                group.filters.splice(filterIndex, 1);
                this.emitChange();
            }
        },

        onFieldChange(filter) {
            filter.dataType = this.getFieldDataType(filter.field);
            filter.operator = "equals";
            filter.value = null;
            this.emitChange();
        },

        getAvailableFields() {
            const fields = [
                { label: "Kişinin Adı", value: "contact.fullname", dataType: "string", hasOptions: false },
                { label: "Kişinin E-postası", value: "contact.email", dataType: "string", hasOptions: false },
                { label: "Kişinin Telefonu", value: "contact.phone", dataType: "string", hasOptions: false },
                { label: "Kişinin WhatsApp Numarası", value: "contact.whatsappNumber", dataType: "string", hasOptions: false },
                { label: "Kişinin WhatsApp Ülkesi", value: "contact.whatsappCountry", dataType: "string", hasOptions: false },
                { label: "Kişinin Platformu", value: "contact.platform", dataType: "string", hasOptions: true },
                { label: "Kişinin Sahibi Var mı", value: "contact.owner.exists", dataType: "boolean", hasOptions: false },
                { label: "Kişinin Sahibi Online mı", value: "contact.owner.isOnline", dataType: "boolean", hasOptions: false },
                { label: "Kişinin Sahibi Durumu", value: "contact.owner.status", dataType: "string", hasOptions: true },
                { label: "Kişinin Yaşı (dakika)", value: "contact.uptime", dataType: "number", hasOptions: false },
                { label: "Kişinin Etiketleri", value: "contact.tags", dataType: "array", hasOptions: true },
                { label: "Kişinin Kanalı", value: "contact.channel", dataType: "string", hasOptions: true },
                { label: "Kişinin Tercih Ettiği Departman", value: "contact.preferredDepartment", dataType: "string", hasOptions: true },
                { label: "Şu An Saat", value: "system.timezone.hour", dataType: "number", hasOptions: false },
                { label: "Şu An Gün", value: "system.timezone.day", dataType: "number", hasOptions: false }
            ];

            if (this.whenType === "chat_opened") {
                fields.push(
                    { label: "Konuşmanın Durumu", value: "conversation.status", dataType: "string", hasOptions: true },
                    { label: "Konuşmanın Departmanı", value: "conversation.subject", dataType: "string", hasOptions: true }
                );
            }

            return fields;
        },

        getFieldDataType(field) {
            const found = this.getAvailableFields().find(f => f.value === field);
            return found ? found.dataType : "string";
        },

        hasOptions(field) {
            const found = this.getAvailableFields().find(f => f.value === field);
            return found ? found.hasOptions : false;
        },

        isMultiSelect(filter) {
            return this.getFieldDataType(filter.field) === 'array' && ['in', 'not_in'].includes(filter.operator);
        },

        getOperatorsForField(field) {
            const dataType = this.getFieldDataType(field);

            if (dataType === "string") {
                return [
                    { label: "eşittir", value: "equals" },
                    { label: "eşit değildir", value: "not_equals" },
                    { label: "içerir", value: "contains" },
                    { label: "içermez", value: "not_contains" },
                    { label: "var", value: "exists" },
                    { label: "yok", value: "not_exists" }
                ];
            }

            if (dataType === "number") {
                return [
                    { label: "eşittir", value: "equals" },
                    { label: "büyüktür", value: "gt" },
                    { label: "büyük veya eşittir", value: "gte" },
                    { label: "küçüktür", value: "lt" },
                    { label: "küçük veya eşittir", value: "lte" },
                    { label: "arasında", value: "between" }
                ];
            }

            if (dataType === "boolean") {
                return [
                    { label: "eşittir", value: "equals" }
                ];
            }

            if (dataType === "array") {
                return [
                    { label: "içerir", value: "in" },
                    { label: "içermez", value: "not_in" }
                ];
            }

            return [];
        },

        needsValue(operator) {
            return !["exists", "not_exists"].includes(operator);
        },

        getFieldOptions(field) {
            if (field === "contact.tags") {
                return this.contactTags;
            }

            if (field === "contact.channel") {
                return this.channels.filter(ch => !ch.isDeleted).map(ch => ({
                    label: ch.name,
                    value: ch._id
                }));
            }

            if (field === "contact.preferredDepartment" || field === "conversation.subject") {
                return this.departments;
            }

            if (field === "contact.platform") {
                return [
                    { label: "WhatsApp", value: "whatsapp" },
                    { label: "Instagram", value: "instagram" },
                    { label: "Telegram", value: "telegram" },
                    { label: "Messenger", value: "messenger" },
                    { label: "LinkedIn", value: "linkedin" },
                    { label: "Web", value: "web" },
                    { label: "Mobile", value: "mobile" },
                    { label: "Email", value: "email" },
                    { label: "Trendyol", value: "trendyol" },
                    { label: "Meta Reklam", value: "meta-leads" },
                    { label: "Supsis", value: "supsis" },
                    { label: "Calendar", value: "calendar" },
                    { label: "Google", value: "google" },
                ];
            }

            if (field === "contact.owner.status") {
                return [
                    { label: "Online", value: "Online" },
                    { label: "Away", value: "Away" },
                    { label: "Busy", value: "Busy" },
                    { label: "Offline", value: "Offline" }
                ];
            }

            if (field === "conversation.status") {
                return [
                    { label: "Chatbot", value: "chatbot" },
                    { label: "Yeni", value: "new" },
                    { label: "Aktif", value: "active" },
                    { label: "Kayıp", value: "lost" },
                    { label: "Sonlandırılmış", value: "destroyed" },
                    { label: "Özel", value: "private" }
                ];
            }

            return [];
        },

        filterOption(input, option) {
            return option.componentOptions.children[0].text.toLowerCase().indexOf(input.toLowerCase()) >= 0;
        }
    }
};
</script>

<style scoped lang="scss">
.filter-builder {
    padding: 20px;
    background: #fafafa;
    border-radius: 8px;

    .empty-filters {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        padding: 60px 20px;
        background: #ffffff;
        border: 2px dashed #d9d9d9;
        border-radius: 12px;

        .empty-icon {
            .material-icons {
                font-size: 64px;
                color: #d9d9d9;
            }
        }

        p {
            color: #999;
            margin: 16px 0 24px 0;
            font-size: 14px;
        }

        button {
            .material-icons {
                font-size: 18px;
                margin-right: 4px;
            }
        }
    }

    .filter-body {
        .filter-group-container {
            margin-bottom: 20px;

            .filter-group {
                background: white;
                border: 1px solid #e0e0e0;
                border-radius: 8px;
                padding: 20px;
                box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);

                .group-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 15px;
                    padding-bottom: 10px;
                    border-bottom: 1px solid #f0f0f0;

                    .group-title {
                        font-weight: 600;
                        color: #666;
                        font-size: 13px;
                        text-transform: uppercase;
                    }
                }

                .filter-row {
                    display: flex;
                    align-items: center;
                    gap: 6px;
                    margin-bottom: 12px;
                    font-size: 15px;
                    line-height: 32px;

                    .filter-text {
                        display: flex;
                        align-items: center;
                        gap: 6px;
                        flex: 1;
                        flex-wrap: wrap;

                        .prefix {
                            color: #666;
                            font-weight: 500;
                            margin-right: 4px;
                        }

                        .prefix-operator {
                            margin-right: 4px;
                        }

                        .multi-value-wrapper {
                            display: inline-flex;
                            align-items: center;
                        }
                    }

                    .remove-btn {
                        opacity: 0;
                        transition: all 0.2s ease;
                        color: #ff4d4f;
                        padding: 4px;
                        min-width: auto;

                        .material-icons {
                            font-size: 18px;
                        }

                        &:hover {
                            background: #fff1f0;
                            transform: scale(1.1);
                        }
                    }

                    &:hover .remove-btn {
                        opacity: 1;
                    }
                }

                .add-condition-btn {
                    display: inline-flex;
                    align-items: center;
                    gap: 6px;
                    padding: 6px 12px;
                    color: #1890ff;
                    cursor: pointer;
                    border-radius: 4px;
                    transition: all 0.2s ease;
                    font-size: 14px;
                    margin-top: 8px;

                    &:hover {
                        background: #e6f7ff;
                    }

                    i {
                        font-size: 18px;
                    }
                }
            }
        }

        .group-operator-row {
            display: flex;
            align-items: center;
            justify-content: center;
            margin: 20px 0;
            gap: 15px;

            .separator-line {
                flex: 1;
                height: 2px;
                background: linear-gradient(to right, transparent, #d9d9d9, transparent);
            }

            .operator-badge {
                background: #f9f0ff;
                padding: 8px 18px;
                border-radius: 20px;
                border: 2px solid #d3adf7;
                box-shadow: 0 2px 8px rgba(146, 84, 222, 0.15);
                transition: all 0.2s ease;

                &:hover {
                    background: #efdbff;
                    border-color: #b37feb;
                    transform: scale(1.02);
                }

                .operator-selector {
                    min-width: 120px;
                    font-weight: 600;
                    font-size: 14px;

                    :deep(.ant-select-selection) {
                        border: none !important;
                        background: transparent !important;
                        box-shadow: none !important;
                        color: #9254de;
                        font-weight: 700;
                    }

                    :deep(.ant-select-arrow) {
                        color: #9254de;
                    }
                }
            }
        }

        .add-group-btn {
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 8px;
            padding: 12px 20px;
            background: white;
            border: 2px dashed #d9d9d9;
            border-radius: 8px;
            cursor: pointer;
            transition: all 0.3s ease;
            margin-top: 20px;
            font-weight: 500;

            &:hover {
                border-color: #1890ff;
                color: #1890ff;
                background: #f0f8ff;
            }

            i {
                font-size: 22px;
            }
        }
    }
}
</style>

<style lang="scss">
.filter-builder {
    .multi-select-inline {
        min-width: 200px !important;
        
        .ant-select-selection {
            border: none !important;
            border-bottom: 2px solid #52c41a !important;
            border-radius: 0 !important;
            box-shadow: none !important;
            background: transparent !important;

            .ant-select-selection__rendered {
                margin: 0 !important;
                padding: 2px 8px !important;
                line-height: 28px !important;
            }

            .ant-select-selection__choice {
                background: #f6ffed !important;
                border: 1px solid #95de64 !important;
                color: #52c41a !important;
                font-weight: 500;
            }

            &:hover {
                background: #f6ffed !important;
            }
        }

        &.ant-select-focused .ant-select-selection {
            border-bottom-color: #389e0d !important;
            box-shadow: 0 2px 4px rgba(82, 196, 26, 0.2) !important;
        }
    }
}
</style>
